<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="640">
    <v-card title="Document Review">
      <v-card-text>
        <div v-if="loading" class="text-center pa-6">
          <v-progress-circular indeterminate color="primary" size="32" />
        </div>

        <div v-else-if="!record" class="text-grey pa-4">
          No extraction found for this document.
        </div>

        <div v-else>
          <v-alert
            v-if="showPatientPicker"
            :type="needsPatientMatch ? 'warning' : 'info'"
            density="compact"
            class="mb-3"
          >
            <div class="mb-2">
              <template v-if="needsPatientMatch">
                This phone number is shared by more than one patient, and the document couldn't be
                confidently matched to one. It hasn't been uploaded to Open Dental yet — pick the
                correct patient below.
              </template>
              <template v-else>
                This phone number is shared by more than one patient. If this document actually
                belongs to someone else in the family, pick the correct patient below.
              </template>
            </div>
            <v-select
              density="compact"
              label="Patient"
              :items="candidatePatientItems"
              item-title="label"
              item-value="contact_id"
              v-model="selectedPatientId"
              hide-details
              bg-color="white"
            />
          </v-alert>

          <div style="display: flex; gap: 16px; flex-wrap: wrap">
            <div style="flex: 1 1 260px; max-width: 300px">
              <ZoomableImage :src="imageUrl" height="260px" />
            </div>

            <div style="flex: 1; min-width: 220px">
              <v-chip size="small" label class="mb-2">
                {{ record.classified_type }}
              </v-chip>

              <v-select
                density="compact"
                label="Document Category"
                :items="odCategories"
                item-title="name"
                item-value="id"
                v-model="selectedCategoryId"
                :loading="categoriesLoading"
                hide-details
                class="mb-3"
              />

              <v-text-field
                density="compact"
                label="Description"
                v-model="selectedShortDescription"
                hide-details
              />
            </div>
          </div>

          <v-alert v-if="unsavedChangeMessage" type="info" density="compact" class="mt-2">
            {{ unsavedChangeMessage }}
          </v-alert>

          <!-- Hidden once a category/description/patient change is pending —
               otherwise it sits right above "Category changed — click Upload
               to Open Dental to apply", implying the doc is both already
               uploaded and not yet uploaded at the same time. -->
          <v-alert v-if="pushResult && !unsavedChangeMessage" :type="pushResult.type" density="compact" class="mt-2">
            {{ pushResult.message }}
          </v-alert>

          <v-btn
            color="primary"
            variant="elevated"
            :loading="uploading"
            @click="uploadToOD"
            :disabled="!canUpload"
            class="mt-3"
          >
            Upload to Open Dental
          </v-btn>

          <div v-if="record.classified_type === 'InsuranceCard'" class="mt-4">
            <v-divider class="mb-3" />
            <div class="text-subtitle-2 mb-2">Insurance</div>

            <v-alert v-if="nonSelfSubscriber" type="warning" density="compact">
              This card may belong to a different subscriber ({{ record.extracted_data?.relationship_to_subscriber }})
              — add it manually in Open Dental.
            </v-alert>

            <template v-else>
              <div v-if="record.patient_insurance_plans && record.patient_insurance_plans.length" class="mb-3">
                <div class="text-caption text-grey-darken-1 mb-1">Existing Insurance on File</div>
                <div
                  v-for="plan in record.patient_insurance_plans"
                  :key="plan.PatPlanNum"
                  class="text-caption"
                  style="line-height: 1.6"
                >
                  {{ ordinalLabel(plan.Ordinal) }}: {{ plan.CarrierName }} (Subscriber {{ plan.SubscriberID || "—" }})
                </div>
              </div>

              <v-autocomplete
                v-model="selectedCarrierNum"
                :items="visibleCarrierItems"
                item-title="CarrierName"
                item-value="CarrierNum"
                label="Insurance Carrier"
                density="compact"
                hide-details
                class="mb-3"
                :loading="carriersLoading"
                :filter="() => true"
                @update:search="carrierSearchQuery = $event"
                @update:menu="onCarrierMenuToggle"
              >
                <template v-slot:item="{ item, props }">
                  <v-list-item
                    v-bind="props"
                    :style="item.raw.isAiMatch ? 'background-color: #E8F0FE' : ''"
                  >
                    <template v-if="item.raw.isAiMatch" v-slot:append>
                      <v-chip size="x-small" color="primary" variant="flat">AI match</v-chip>
                    </template>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <v-select
                v-if="selectedCarrierNum"
                v-model="selectedInsPlanNum"
                :items="insPlanItems"
                item-title="title"
                item-value="value"
                label="Insurance Plan"
                density="compact"
                hide-details
                class="mb-3"
                :loading="insPlansLoading"
              />

              <template v-if="selectedInsPlanNum === 'NEW'">
                <v-text-field density="compact" label="Group Name" v-model="insuranceGroupName" hide-details class="mb-3" />
                <v-select
                  density="compact"
                  label="Plan Type"
                  :items="planTypeItems"
                  item-title="title"
                  item-value="value"
                  v-model="insurancePlanType"
                  hide-details
                  class="mb-3"
                  :loading="planTypesLoading"
                />
                <v-select
                  density="compact"
                  label="Fee Schedule (optional)"
                  :items="feeSchedItems"
                  item-title="title"
                  item-value="value"
                  v-model="insuranceFeeSchedNum"
                  hide-details
                  class="mb-3"
                  clearable
                  :loading="feeSchedulesLoading"
                />
              </template>
              <v-text-field density="compact" label="Subscriber ID" v-model="insuranceSubscriberId" hide-details class="mb-3" />
              <v-select
                density="compact"
                label="Ordinal"
                :items="[{ title: 'Primary', value: 1 }, { title: 'Secondary', value: 2 }, { title: 'Tertiary', value: 3 }]"
                v-model="insuranceOrdinal"
                hide-details
                class="mb-3"
              />

              <div v-if="record.insurance_status" class="d-flex align-center mb-2" style="gap: 6px; font-size: 12px">
                <template v-if="record.insurance_status === 'added'">
                  <v-icon size="14" color="green">mdi-check-circle-outline</v-icon>
                  <span>Added to Open Dental</span>
                </template>
                <template v-else>
                  <v-icon size="14" color="error">mdi-alert-circle-outline</v-icon>
                  <span>Failed to add — try again</span>
                </template>
              </div>

              <v-btn
                v-if="canWriteInsurance"
                size="small"
                color="secondary"
                variant="outlined"
                :loading="submittingInsurance"
                :disabled="!canSubmitInsurance"
                @click="submitInsurance"
              >
                Add to Open Dental Insurance
              </v-btn>
              <div v-else class="text-caption text-medium-emphasis">
                Only Claims Specialists can add insurance to Open Dental.
              </div>

              <v-alert v-if="insuranceResult" :type="insuranceResult.type" density="compact" class="mt-2">
                {{ insuranceResult.message }}
              </v-alert>

              <v-alert v-if="insuranceConflict && canWriteInsurance" type="warning" density="compact" class="mt-2">
                <div class="mb-2">
                  This patient already has {{ ordinalLabel(insuranceConflict.existing.Ordinal) }} insurance on
                  file: {{ insuranceConflict.existing.CarrierName }} (Subscriber
                  {{ insuranceConflict.existing.SubscriberID || "—" }}). Replace it with this new insurance, or
                  add this as {{ ordinalLabel(insuranceConflict.suggested_ordinal) }} instead?
                </div>
                <div class="d-flex" style="gap: 8px">
                  <v-btn
                    size="small"
                    color="warning"
                    variant="flat"
                    :loading="submittingInsurance"
                    @click="resolveConflict('replace')"
                  >
                    Replace Existing
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="outlined"
                    :loading="submittingInsurance"
                    @click="resolveConflict('add')"
                  >
                    Add as {{ ordinalLabel(insuranceConflict.suggested_ordinal) }}
                  </v-btn>
                </div>
              </v-alert>
            </template>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('update:modelValue', false)">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import odService from "../../../services/odService";
import ZoomableImage from "../../UIComponents/ZoomableImage.vue";

export default {
  name: "DocumentExtractionDialog",
  components: { ZoomableImage },
  props: {
    modelValue: { type: Boolean, required: true },
    mediaId: { type: [Number, String], default: null },
    contactId: { type: [Number, String], default: null },
  },
  emits: ["update:modelValue", "pushed"],
  data() {
    return {
      loading: false,
      uploading: false,
      record: null,
      pushResult: null,
      selectedPatientId: null,
      selectedCategoryId: null,
      selectedShortDescription: "",
      odCategories: [],
      categoriesLoading: false,
      categoriesLoaded: false,
      allCarriers: [],
      carriersLoading: false,
      carriersLoaded: false,
      allPlanTypes: [],
      planTypesLoading: false,
      planTypesLoaded: false,
      allFeeSchedules: [],
      feeSchedulesLoading: false,
      feeSchedulesLoaded: false,
      selectedCarrierNum: null,
      // Tracked separately from the field's displayed text — Vuetify
      // reuses the selected item's own (decorated) title as its internal
      // search text whenever the menu reopens with a value already
      // selected, which left the list looking empty since nothing else
      // matches that exact string. This stays purely "what the user is
      // actively typing," reset on every menu open (see onCarrierMenuToggle).
      carrierSearchQuery: "",
      insPlanResults: [],
      insPlansLoading: false,
      selectedInsPlanNum: null,
      insuranceGroupName: "",
      insurancePlanType: "",
      insuranceFeeSchedNum: null,
      insuranceSubscriberId: "",
      insuranceOrdinal: 1,
      submittingInsurance: false,
      insuranceResult: null,
      insuranceConflict: null,
      // Set when pre-filling the form from a previously-confirmed OD
      // insurance record (confirmed_insurance) — the Plan dropdown can't be
      // selected until its list finishes loading for the carrier, which
      // happens asynchronously in the selectedCarrierNum watcher below.
      pendingConfirmedPlanNum: null,
    };
  },
  computed: {
    ...mapGetters(["isAdmin", "isClaimsSpecialist"]),
    canWriteInsurance() {
      return this.isClaimsSpecialist || this.isAdmin;
    },
    categoryChanged() {
      return !!this.record && this.selectedCategoryId !== this.record.od_category_id;
    },
    descriptionChanged() {
      return !!this.record && this.selectedShortDescription !== (this.record.extracted_data?.short_description || "");
    },
    // Set by classifyAndUploadDocument() when a shared phone number left
    // this document's patient ambiguous and no confident auto-match was
    // found — the OD upload was withheld pending this confirmation.
    needsPatientMatch() {
      return this.record?.upload_status === "pending_patient_match";
    },
    candidatePatientItems() {
      return (this.record?.candidate_contacts || []).map((c) => ({
        contact_id: c.contact_id,
        label: `${c.first_name || ""} ${c.last_name || ""}`.trim() +
          (c.Birthdate ? ` (DOB ${new Date(c.Birthdate).toLocaleDateString()})` : ""),
      }));
    },
    // Show the picker whenever there's genuinely more than one family
    // member to choose from — not just when the system flagged ambiguity —
    // so staff can correct a confident-but-wrong auto-match too.
    showPatientPicker() {
      return this.needsPatientMatch || this.candidatePatientItems.length > 1;
    },
    patientSelectionChanged() {
      return this.selectedPatientId != null && this.selectedPatientId !== this.record?.current_contact_id;
    },
    // Nudges staff to actually commit a picker change — selecting a patient
    // or category alone does nothing until "Upload to Open Dental" is
    // clicked, which isn't obvious from the pickers alone.
    unsavedChangeMessage() {
      const changed = [];
      if (this.patientSelectionChanged) changed.push("Patient");
      if (this.categoryChanged) changed.push("Category");
      if (this.descriptionChanged) changed.push("Description");
      if (!changed.length) return "";
      return `${changed.join(" and ")} changed — click "Upload to Open Dental" to apply.`;
    },
    // Requires a patient + category to be selected at all; once the document
    // is already successfully uploaded, also requires something to have
    // actually changed, so a no-op click can't spawn a duplicate Open Dental
    // document under the same patient/category/description.
    canUpload() {
      if (!this.selectedPatientId || !this.selectedCategoryId) return false;
      if (this.record?.upload_status !== "uploaded") return true;
      return this.patientSelectionChanged || this.categoryChanged || this.descriptionChanged;
    },
    // v1 only supports the patient being their own subscriber — the AI's
    // relationship_to_subscriber guess is unreliable (most physical cards
    // don't print it at all) and resolving a different family member as
    // subscriber needs matching against this app's own contact/family data,
    // real added complexity left for a later pass.
    nonSelfSubscriber() {
      const rel = (this.record?.extracted_data?.relationship_to_subscriber || "").trim().toLowerCase();
      if (!rel) return false;
      return !["self", "patient"].includes(rel);
    },
    // Matches the AI-extracted insurance_company text against the loaded
    // carrier list (case-insensitive, either direction — handles "Delta
    // Dental" extracted against a "Delta Dental PPO" carrier row and the
    // reverse). Duplicate-named carriers are common real OD data, so among
    // all plausible matches the one with the most subscribers wins — same
    // "most established" heuristic already used to order the dropdown.
    aiMatchedCarrierNum() {
      const extracted = (this.record?.extracted_data?.insurance_company || "").trim().toLowerCase();
      if (!extracted || !this.allCarriers.length) return null;

      // Ranks by match quality first (exact, then closest-length partial
      // match) so a precise match always beats a broader/more generic one —
      // subscriber count only breaks ties between equally-good matches
      // (the genuine duplicate-named-carrier case), rather than overriding
      // match quality, which previously let a generic high-volume carrier
      // win over a more specific correct one just for having more subscribers.
      const scored = this.allCarriers
        .map((c) => {
          const name = (c.CarrierName || "").trim().toLowerCase();
          if (!name) return null;
          let tier;
          if (name === extracted) tier = 0;
          else if (name.includes(extracted) || extracted.includes(name)) tier = 1;
          else return null;
          return { carrier: c, tier, lengthDiff: Math.abs(name.length - extracted.length) };
        })
        .filter(Boolean);
      if (!scored.length) return null;

      scored.sort((a, b) =>
        a.tier - b.tier ||
        a.lengthDiff - b.lengthDiff ||
        (b.carrier.SubscriberCount || 0) - (a.carrier.SubscriberCount || 0)
      );
      return scored[0].carrier.CarrierNum;
    },
    // Decorates each carrier with its plan/subscriber counts so staff can
    // tell same-named duplicate carriers apart (real OD data — separate rows
    // per employer group/region, or accidental duplicate entry over time).
    // The real name still leads the string, so typing it still filters
    // correctly via Vuetify's default matching. isAiMatch flags the
    // auto-selected carrier so it's visually identifiable in the list.
    carrierItems() {
      return this.allCarriers.map((c) => {
        const plans = c.PlanCount || 0;
        const subs = c.SubscriberCount || 0;
        return {
          CarrierNum: c.CarrierNum,
          CarrierName: `${c.CarrierName} (${plans} plan${plans === 1 ? "" : "s"}, ${subs} subscriber${subs === 1 ? "" : "s"})`,
          isAiMatch: c.CarrierNum === this.aiMatchedCarrierNum,
        };
      });
    },
    // What the dropdown actually renders — carrierItems filtered by our own
    // tracked search text (carrierSearchQuery), completely independent of
    // whatever's currently selected. Vuetify's built-in filter is disabled
    // (:filter="() => true" on the autocomplete) in favor of this.
    visibleCarrierItems() {
      const q = this.carrierSearchQuery.trim().toLowerCase();
      if (!q) return this.carrierItems;
      return this.carrierItems.filter((c) => (c.CarrierName || "").toLowerCase().includes(q));
    },
    // "+ Create new plan" always shown first, followed by this carrier's
    // existing plans (each labeled with its group name/plan type so staff
    // can tell them apart).
    insPlanItems() {
      const existing = this.insPlanResults.map((p) => {
        const count = p.SubscriberCount || 0;
        return {
          title: `${p.GroupName || "(no group name)"}${p.PlanType ? " — " + p.PlanType : ""} (${count} subscriber${count === 1 ? "" : "s"})`,
          value: p.PlanNum,
        };
      });
      return [{ title: "+ Create new plan", value: "NEW" }, ...existing];
    },
    // Raw PlanType values already in use in this practice's own OD data —
    // shown as-is rather than guessed/human-labeled (see loadPlanTypesOnce),
    // except blank, which is given a readable label while still submitting ''.
    planTypeItems() {
      return this.allPlanTypes.map((pt) => ({ title: pt ? pt : "Standard/PPO (blank)", value: pt }));
    },
    feeSchedItems() {
      return this.allFeeSchedules.map((fs) => ({ title: fs.Description, value: fs.FeeSchedNum }));
    },
    canSubmitInsurance() {
      if (!this.selectedCarrierNum || !this.selectedInsPlanNum || !this.insuranceSubscriberId || !this.insuranceOrdinal) {
        return false;
      }
      if (this.selectedInsPlanNum === "NEW" && !this.insuranceGroupName) return false;
      return true;
    },
    imageUrl() {
      if (!this.contactId || !this.mediaId) return "";
      // Absolute URL, not a relative "/api/..." path: in local dev the
      // frontend (vue-cli-service, :8081) and backend (:3000) are separate
      // origins with no devServer proxy configured, so a relative path
      // would resolve against the frontend dev server, which has no such
      // route. Only works as a relative path in production, where the
      // backend itself serves the built frontend from the same origin.
      return `${process.env.VUE_APP_API_URL}/api/contact/${this.contactId}/media/${this.mediaId}`;
    },
  },
  watch: {
    // Refreshes the Plan dropdown for the newly picked carrier and clears any
    // stale selection from a previous carrier so it's never accidentally
    // submitted against the wrong one.
    selectedCarrierNum(carrierNum) {
      this.selectedInsPlanNum = null;
      this.insPlanResults = [];
      if (!carrierNum) return;
      this.insPlansLoading = true;
      odService
        .searchOdInsPlans(carrierNum)
        .then((res) => {
          this.insPlanResults = res.data?.data || [];
          // If this carrier was set while pre-filling from a previously-
          // confirmed OD insurance record, select that exact plan now that
          // the list has loaded, instead of leaving the field blank.
          if (this.pendingConfirmedPlanNum && this.insPlanResults.some((p) => p.PlanNum === this.pendingConfirmedPlanNum)) {
            this.selectedInsPlanNum = this.pendingConfirmedPlanNum;
          }
          this.pendingConfirmedPlanNum = null;
        })
        .catch((err) => {
          console.error("Failed to load Open Dental insurance plans:", err);
        })
        .finally(() => {
          this.insPlansLoading = false;
        });
    },
    modelValue(open) {
      if (open) {
        this.fetchExtraction();
      } else {
        this.record = null;
        this.pushResult = null;
        this.selectedPatientId = null;
        this.selectedCategoryId = null;
        this.selectedShortDescription = "";
        this.selectedCarrierNum = null;
        this.carrierSearchQuery = "";
        this.insPlanResults = [];
        this.selectedInsPlanNum = null;
        this.insuranceGroupName = "";
        this.insurancePlanType = "";
        this.insuranceFeeSchedNum = null;
        this.insuranceSubscriberId = "";
        this.insuranceOrdinal = 1;
        this.insuranceResult = null;
        this.insuranceConflict = null;
        this.pendingConfirmedPlanNum = null;
      }
    },
  },
  methods: {
    async fetchExtraction() {
      if (!this.mediaId) return;
      this.loading = true;
      this.pushResult = null;
      try {
        const [extractionRes] = await Promise.all([
          odService.getMediaExtraction(this.mediaId),
          this.loadCategoriesOnce(),
          this.loadCarriersOnce(),
          this.loadPlanTypesOnce(),
          this.loadFeeSchedulesOnce(),
        ]);
        const res = extractionRes;
        this.record = res.data;
        this.selectedCategoryId = res.data.od_category_id;
        this.selectedPatientId = res.data.current_contact_id;
        this.selectedShortDescription = res.data.extracted_data?.short_description || "";
        this.insuranceConflict = null;

        const confirmed = res.data.confirmed_insurance;
        if (confirmed) {
          // Show what's actually confirmed in OD, not the raw AI guess —
          // this is a live lookup (see getPatientInsurancePlans on the
          // backend), so it stays correct even if changed directly in OD
          // after the fact (e.g. Ordinal reordered).
          this.insuranceGroupName = confirmed.GroupName || "";
          // Only pre-select if this practice's own OD data still contains
          // that PlanType (see loadPlanTypesOnce) — otherwise leave it
          // unselected rather than injecting a stale/invalid dropdown option.
          this.insurancePlanType = this.allPlanTypes.includes(confirmed.PlanType || "") ? (confirmed.PlanType || "") : "";
          this.insuranceFeeSchedNum = null; // not exposed by the confirmed-insurance lookup
          this.insuranceSubscriberId = confirmed.SubscriberID || "";
          this.insuranceOrdinal = confirmed.Ordinal || 1;
          this.pendingConfirmedPlanNum = confirmed.PlanNum;
          this.selectedCarrierNum = confirmed.CarrierNum;
        } else {
          this.insuranceGroupName = res.data.extracted_data?.group_name || "";
          const extractedPlanType = res.data.extracted_data?.plan_type || "";
          this.insurancePlanType = this.allPlanTypes.includes(extractedPlanType) ? extractedPlanType : "";
          this.insuranceFeeSchedNum = null;
          this.insuranceSubscriberId = res.data.extracted_data?.subscriber_id || "";
          this.insuranceOrdinal = 1;
          this.pendingConfirmedPlanNum = null;
          // Defaults to the AI-matched carrier (see aiMatchedCarrierNum) when
          // one was found — allCarriers is guaranteed loaded by the Promise.all
          // above, so the match is available as soon as record is set.
          this.selectedCarrierNum = this.aiMatchedCarrierNum;
        }
        this.carrierSearchQuery = "";
        this.selectedInsPlanNum = null;
        this.insPlanResults = [];
      } catch (err) {
        console.error("Failed to load document extraction:", err);
        this.record = null;
      } finally {
        this.loading = false;
      }
    },
    // Loaded once per session (not per-open) — the same client's Open
    // Dental document categories don't change between documents.
    async loadCategoriesOnce() {
      if (this.categoriesLoaded || this.categoriesLoading) return;
      this.categoriesLoading = true;
      try {
        const res = await odService.getImageCategories();
        this.odCategories = (res.data || []).map((c) => ({ id: c.Id, name: c.Name }));
        this.categoriesLoaded = true;
      } catch (err) {
        console.error("Failed to load Open Dental document categories:", err);
      } finally {
        this.categoriesLoading = false;
      }
    },
    // Loaded once per session (not per-open) — carriers are a fairly static,
    // practice-wide reference list, so the whole thing is fetched once and
    // filtered client-side via the autocomplete's own default filtering,
    // rather than re-querying Open Dental on every keystroke.
    async loadCarriersOnce() {
      if (this.carriersLoaded || this.carriersLoading) return;
      this.carriersLoading = true;
      try {
        const res = await odService.listOdCarriers();
        this.allCarriers = res.data?.data || [];
        this.carriersLoaded = true;
      } catch (err) {
        console.error("Failed to load Open Dental carriers:", err);
      } finally {
        this.carriersLoading = false;
      }
    },
    // Loaded once per session, same rationale as loadCarriersOnce() — the
    // practice's set of PlanType values already in use across its own
    // insplan rows doesn't change within a document-review session.
    async loadPlanTypesOnce() {
      if (this.planTypesLoaded || this.planTypesLoading) return;
      this.planTypesLoading = true;
      try {
        const res = await odService.listOdPlanTypes();
        this.allPlanTypes = res.data?.data || [];
        this.planTypesLoaded = true;
      } catch (err) {
        console.error("Failed to load Open Dental plan types:", err);
      } finally {
        this.planTypesLoading = false;
      }
    },
    async loadFeeSchedulesOnce() {
      if (this.feeSchedulesLoaded || this.feeSchedulesLoading) return;
      this.feeSchedulesLoading = true;
      try {
        const res = await odService.listOdFeeSchedules();
        this.allFeeSchedules = res.data?.data || [];
        this.feeSchedulesLoaded = true;
      } catch (err) {
        console.error("Failed to load Open Dental fee schedules:", err);
      } finally {
        this.feeSchedulesLoading = false;
      }
    },
    async uploadToOD() {
      if (!this.canUpload || !this.mediaId) return;
      this.uploading = true;
      this.pushResult = null;
      try {
        const res = await odService.uploadMediaToOD(
          this.mediaId,
          this.selectedPatientId,
          this.selectedCategoryId,
          this.selectedShortDescription
        );
        this.record = res.data;
        this.selectedPatientId = res.data.current_contact_id;
        this.selectedCategoryId = res.data.od_category_id;
        this.selectedShortDescription = res.data.extracted_data?.short_description || "";
        if (res.data.previously_uploaded_elsewhere) {
          this.pushResult = {
            type: "warning",
            message: "Re-uploaded under the new patient. The earlier copy is still in the previous patient's Open Dental chart — please remove it there manually.",
          };
        } else {
          this.pushResult = { type: "success", message: "Uploaded to Open Dental." };
        }
        this.$emit("pushed");
      } catch (err) {
        console.error("Failed to upload document to Open Dental:", err);
        this.pushResult = { type: "error", message: "Failed to upload document to Open Dental." };
      } finally {
        this.uploading = false;
      }
    },
    // Resets the search on every open (regardless of what's already
    // selected) so the full list is always what staff see first — only
    // narrows once they actually type. Left as-is on close so the closed
    // field still displays the selected carrier's name.
    onCarrierMenuToggle(isOpen) {
      if (isOpen) this.carrierSearchQuery = "";
    },
    ordinalLabel(ordinal) {
      if (ordinal === 1) return "Primary";
      if (ordinal === 2) return "Secondary";
      if (ordinal === 3) return "Tertiary";
      return `Ordinal ${ordinal}`;
    },
    async submitInsurance() {
      if (!this.canSubmitInsurance || !this.mediaId) return;
      await this.doSubmitInsurance(false);
    },
    // Staff's response to the Ordinal-conflict alert: either replace the
    // existing PatPlan's insurance, or fall through to the next open
    // Ordinal instead — never silently picked for them.
    async resolveConflict(action) {
      if (!this.insuranceConflict) return;
      if (action === "add") {
        this.insuranceOrdinal = this.insuranceConflict.suggested_ordinal;
      }
      const replace = action === "replace";
      this.insuranceConflict = null;
      await this.doSubmitInsurance(replace);
    },
    async doSubmitInsurance(replace) {
      if (!this.mediaId) return;
      this.submittingInsurance = true;
      this.insuranceResult = null;
      this.insuranceConflict = null;
      try {
        const res = await odService.confirmMediaInsurance(this.mediaId, {
          carrier_num: this.selectedCarrierNum,
          ins_plan_num: this.selectedInsPlanNum,
          group_name: this.insuranceGroupName,
          plan_type: this.insurancePlanType,
          fee_sched_num: this.insuranceFeeSchedNum,
          subscriber_id: this.insuranceSubscriberId,
          ordinal: this.insuranceOrdinal,
          replace,
        });
        this.record = res.data;
        if (res.data.insurance_status === "added") {
          this.insuranceResult = { type: "success", message: "Insurance added to Open Dental." };
        } else {
          this.insuranceResult = { type: "error", message: "Failed to add insurance to Open Dental." };
        }
      } catch (err) {
        if (err.response?.status === 409 && err.response?.data?.conflict) {
          this.insuranceConflict = err.response.data;
        } else {
          console.error("Failed to add insurance to Open Dental:", err);
          this.insuranceResult = { type: "error", message: "Failed to add insurance to Open Dental." };
        }
      } finally {
        this.submittingInsurance = false;
      }
    },
  },
};
</script>
